import type { Fragment } from "../types.js";
import { colors, brandUrl } from "../../brand/theme.js";
import { escapeHtml } from "../composition.js";
import { tl } from "../timeline.js";

/**
 * CTA pill — large gradient button with optional URL caption below.
 * Animation: scale-in (0.85 → 1) over 0.6s starting at `startSec`, then a
 * looping 2s pulse (scale × 1.04 → 1) for the remainder of the visibility.
 */
export function cta(opts: {
  id: string;
  text: string;
  startSec: number;
  /** Seconds the CTA stays on screen — controls how many pulses we emit. */
  durationSec: number;
  size?: number;
  url?: string;
}): Fragment {
  const { id, text, startSec, durationSec, size = 56, url } = opts;
  const captionUrl = url ?? brandUrl;

  const padV = Math.round(size * 0.5);
  const padH = Math.round(size * 1.2);
  const captionSize = Math.round(size * 0.45);

  const html = `<div id="${id}" class="cta-wrapper" style="display:inline-flex;flex-direction:column;gap:16px;align-items:center;">
  <div class="cta-pill" style="padding:${padV}px ${padH}px;border-radius:9999px;background:linear-gradient(135deg, ${colors.primary}, ${colors.secondary});box-shadow:0 24px 60px ${colors.primary}66;color:#fff;font-weight:800;font-size:${size}px;letter-spacing:-0.5px;transform-origin:center;">${escapeHtml(text)}</div>
  ${captionUrl ? `<span class="cta-url" style="color:${colors.muted};font-weight:600;font-size:${captionSize}px;">${escapeHtml(captionUrl.replace(/^https?:\/\//, ""))}</span>` : ""}
</div>`;

  const tweens: string[] = [
    tl.fromTo(
      `#${id} .cta-pill`,
      { scale: 0.85 },
      { scale: 1, duration: 0.8, ease: "back.out(2)" },
      startSec,
    ),
    tl.fromTo(
      `#${id} .cta-url`,
      { opacity: 0 },
      { opacity: 1, duration: 0.8, ease: "power2.out" },
      startSec,
    ),
  ];

  // Pulses — every 2 seconds within visibility, scale 1.04 then back.
  const pulseStart = startSec + 0.8;
  const pulseEnd = startSec + durationSec;
  for (let t = pulseStart; t + 2 <= pulseEnd; t += 2) {
    tweens.push(tl.to(`#${id} .cta-pill`, { scale: 1.04, duration: 1, ease: "sine.inOut" }, t));
    tweens.push(tl.to(`#${id} .cta-pill`, { scale: 1, duration: 1, ease: "sine.inOut" }, t + 1));
  }

  const init = `gsap.set(${JSON.stringify(`#${id} .cta-pill`)}, { scale: 0.85, transformOrigin: "center" });
gsap.set(${JSON.stringify(`#${id} .cta-url`)}, { opacity: 0 });`;

  return { html, init, tweens };
}
